import { useCallback, useMemo, type ReactNode } from 'react'
import { useToast } from '../../context/useToast'
import { useOrbit } from '../Common/Orbit/useOrbit'
import { withLcmChip } from '../Common/Orbit/lcmOrbitSlices'
import { useLcmChipDispatchers } from '../Common/Orbit/useLcmChipDispatchers'
import type { OrbitActionBridge } from '../Common/Orbit/orbitSliceHelpers'
import { buildBandHeaderSlices, buildEventMarkerSlices, buildTimelineCanvasSlices } from './timeline.orbit'
import { resolveOrbitActionId } from '../Common/Orbit/orbitSliceIdMap'
import { invokeAction } from '../../services/actions/registry'
import { motion } from 'framer-motion'
import { Pressable } from '../Common/Pressable'
import { useTimeline } from '../../context/useTimeline'
import type { TimelineElement } from '../../context/TimelineContext'
import { useClientPreferences } from '../../context/useClientPreferences'
import { useNavigation } from '../../context/useNavigation'
import { useWorkspace } from '../../context/useWorkspace'
import { useResourceReadiness } from '../../hooks/useResourceReadiness'
import { CanvasOverlay } from '../Canvas/CanvasOverlay'
import { usePannableCanvasContext } from '../Canvas/pannableCanvasContext'
import { useNls } from '../../nls/nlsProvider'
import { PathOnlyRow } from '../Common/PathOnlyRow'
import type { ChecklistItem } from '../../types/schema'
import type { ResourceReadiness } from '../../types/resource'
import type { DashboardViewProps } from '../DashboardContainer'
import type {
  DashboardChecklistVisualItem,
  RecurringOccurrenceProjection,
} from '../../types/dashboardVisualItem'
import {
  buildScaleMarkers,
  elementEndMs,
  elementStartMs,
  formatElementRange,
  getElementColor,
  getHoverTitle,
  getLayoutTransition,
  getRangeStyle,
  splitMacroElements,
} from './viewUtils'
import { TimeScale } from './TimeScale'

const BAR_HEIGHT = 42
const CHILD_STRIP_HEIGHT = 18
const ROW_HEIGHT = BAR_HEIGHT + 14
const CADENCE_TRAIL_HEIGHT = 6
const DAY_MS = 24 * 60 * 60 * 1000
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const buildWeekendBands = (startMs: number, endMs: number) => {
  const duration = Math.max(1, endMs - startMs)
  const visibleDays = duration / DAY_MS
  if (visibleDays > 60) return []
  const bands: { left: string; width: string }[] = []
  const cursor = new Date(startMs)
  cursor.setHours(0, 0, 0, 0)
  const daysToSat = (6 - cursor.getDay() + 7) % 7
  cursor.setDate(cursor.getDate() + daysToSat)
  while (cursor.getTime() < endMs) {
    const bandStart = Math.max(cursor.getTime(), startMs)
    const bandEnd = Math.min(cursor.getTime() + 2 * DAY_MS, endMs)
    if (bandEnd > bandStart) {
      const left = ((bandStart - startMs) / duration) * 100
      const width = ((bandEnd - bandStart) / duration) * 100
      bands.push({ left: `${left}%`, width: `${width}%` })
    }
    cursor.setDate(cursor.getDate() + 7)
  }
  return bands
}
const checklistDateMs = (item: DashboardChecklistVisualItem) => {
  const raw = item.startDate ?? item.endDate
  if (!raw) return null
  const parsed = new Date(raw).getTime()
  return Number.isFinite(parsed) ? parsed : null
}

/** State-driven visual language for a checklist keyframe marker. Consumes the
 *  fields the projection now derives so views don't re-check dates. Keeps the
 *  "calm state truly recedes" guardrail from the plan's Anti-clutter mechanics. */
const markerStyleFor = (item: DashboardChecklistVisualItem) => {
  const workflow = item.workflowStatus
  const isPostponed = workflow === 'POSTPONED' || workflow === 'NOT_REQUIRED'
  const isBlocked = item.isCompletionBlocked
  const isOverdue = !item.isCompleted && item.dueDateRelativity === 'past'
  const isDueToday = !item.isCompleted && item.dueDateRelativity === 'today'
  if (item.isCompleted) {
    return { fill: 'bg-[var(--success)]', border: 'border-[var(--success)]', opacity: 'opacity-60', label: 'Done' }
  }
  if (isPostponed) {
    return { fill: 'bg-[var(--text-strong)]', border: 'border-[var(--border-subtle)]', opacity: 'opacity-35', label: 'Postponed' }
  }
  if (isBlocked) {
    return { fill: 'bg-[var(--danger)]', border: 'border-[var(--danger)]', opacity: 'opacity-100', label: 'Blocked' }
  }
  if (isOverdue) {
    return { fill: 'bg-[var(--warning)]', border: 'border-[var(--warning)]', opacity: 'opacity-100', label: 'Overdue' }
  }
  if (isDueToday) {
    return { fill: 'bg-[var(--info)]', border: 'border-[var(--border)]', opacity: 'opacity-100', label: 'Due today' }
  }
  return { fill: 'bg-[var(--text-strong)]', border: 'border-[var(--border-strong)]', opacity: 'opacity-100', label: 'Open' }
}

/** Per-occurrence dot styling for the cadence trail. Same state vocabulary
 *  as `markerStyleFor` so the visual grammar stays consistent. */
const cadenceDotClass = (state: RecurringOccurrenceProjection['state']) => {
  switch (state) {
    case 'completed': return 'bg-[var(--success)] border-[var(--success)]'
    case 'today':     return 'bg-[var(--info)] border-[var(--border)] ring-1 ring-[var(--border-strong)]'
    case 'missed':    return 'bg-[color-mix(in_srgb,var(--danger)_80%,transparent)] border-[var(--danger)]'
    case 'blocked':   return 'bg-[var(--danger)] border-[var(--danger)]'
    case 'upcoming':
    default:          return 'bg-[color-mix(in_srgb,var(--text-strong)_25%,transparent)] border-[var(--border)]'
  }
}

const packRows = (items: TimelineElement[]) => {
  const sorted = [...items].sort((first, second) => elementStartMs(first) - elementStartMs(second))
  return sorted.map((item) => [item])
}

const getSubTaskStyle = (subTask: TimelineElement, task: TimelineElement) => {
  const taskStart = elementStartMs(task)
  const taskDuration = Math.max(1, elementEndMs(task) - taskStart)
  const subStart = elementStartMs(subTask)
  const subLeft = ((subStart - taskStart) / taskDuration) * 100
  const subWidth = (Math.max(1, elementEndMs(subTask) - subStart) / taskDuration) * 100

  return {
    left: `${Math.min(100, Math.max(0, subLeft))}%`,
    width: `${Math.min(100, Math.max(1, subWidth))}%`,
  }
}

const formatElementsRange = (items: TimelineElement[]) => {
  const dated = items.filter((item) => item.startDate && item.endDate)
  if (dated.length === 0) return 'No dates'

  const startMs = Math.min(...dated.map(elementStartMs))
  const endMs = Math.max(...dated.map(elementEndMs))
  const formatter = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' })
  return `${formatter.format(new Date(startMs))} - ${formatter.format(new Date(endMs))}`
}

const calculateAverageProgress = (items: TimelineElement[], getProgress: (id: string) => number) => {
  if (items.length === 0) return 0
  const totalProgress = items.reduce((total, item) => total + getProgress(item.id), 0)
  return Math.round(totalProgress / items.length)
}

export const TimelineView = ({
  elements,
  visualItems,
  viewStartMs,
  viewEndMs,
  selectedElementId,
  onElementClick,
  onChecklistClick,
  todayContext,
  recurringOccurrencesById,
}: DashboardViewProps) => {
  const { activeWorkspaceId, workspaces, canPerform } = useWorkspace()
  const { showToast } = useToast()
  const lcmDispatchers = useLcmChipDispatchers()
  const { t } = useNls()
  const {
    calculateElementProgress,
    checklistItems: flatChecklistItems,
    createElement,
    deleteElement,
    pathOnlyElementIds = new Set(),
    isAccessRestricted = false,
  } = useTimeline()
  const onOrbitAction = useCallback<OrbitActionBridge>((sliceId, label, id, title, phase6) => {
    if (phase6) { showToast('Coming in Orbit Phase 6'); return }
    const actionId = resolveOrbitActionId(sliceId, 'timeline')
    if (actionId) {
      void invokeAction(actionId, { id, title }, {
        workspaceId: activeWorkspaceId ?? '',
        actor: { uid: '' },
        canPerform,
        showToast,
        invokedFrom: 'orbit',
        timelineOps: {
          editEvent: (eventId: string) => {
            const el = elements.find((e) => e.id === eventId)
            if (el) onElementClick?.(el)
          },
          createEvent: async () => {
            await createElement?.({
              type: 'TASK',
              title: 'New Event',
              parentId: null,
              progress: 0,
              weight: 1,
            })
          },
          duplicateEvent: async (eventId: string) => {
            const el = elements.find((e) => e.id === eventId)
            if (el) {
              await createElement?.({
                ...el,
                title: `${el.title} (Copy)`,
              })
            }
          },
          deleteEvent: async (delId: string) => {
            await deleteElement?.(delId)
          },
          fitToRange: () => {
            showToast('Fit to range', 'info')
          },
          collapseBand: (bandId: string) => {
            showToast(`Band collapsed · ${bandId}`, 'info')
          },
        },
      })
      return
    }
    const lcmDispatch = lcmDispatchers[sliceId]
    if (lcmDispatch) { lcmDispatch(id, title); return }
    console.info('[orbit] unwired', sliceId)
    showToast(`${label}${title ? ` · ${title}` : ''}`)
    // TODO(orbit-handler-missing): sliceIds that hit this fallback still need real primitives — grep console for "[orbit] unwired" to enumerate.
  }, [activeWorkspaceId, canPerform, createElement, deleteElement, elements, lcmDispatchers, onElementClick, showToast])
  const orbitDescriptor = useMemo(() => ({
    page: 'timeline',
    centerLabel: 'Timeline',
    onCreate: () => {
      void createElement?.({
        type: 'TASK',
        title: 'New Event',
        parentId: null,
        progress: 0,
        weight: 1,
      })
    },
    contextSlices: () => [],
    targets: withLcmChip({
      'event-marker': (node: HTMLElement) => buildEventMarkerSlices(node, onOrbitAction),
      'band-header': (node: HTMLElement) => buildBandHeaderSlices(node, onOrbitAction),
      canvas: () => buildTimelineCanvasSlices(onOrbitAction),
    }, onOrbitAction),
  }), [createElement, onOrbitAction])
  useOrbit(orbitDescriptor)
  const { preferences } = useClientPreferences()
  const { currentLevel, activeGoalId, activeTaskId, activeSubTaskId } = useNavigation()
  const { transform: canvasTransform } = usePannableCanvasContext()
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId) ?? null
  const resourceReadiness = useResourceReadiness(activeWorkspace, flatChecklistItems ?? [], elements)
  const readinessForElement = (element: TimelineElement): ResourceReadiness =>
    resourceReadiness.getSignalsForNode(element as unknown as ChecklistItem)
  const readinessTooltipSuffix = (readiness: ResourceReadiness) => {
    const visible = readiness.signals.filter((signal) => signal.severity !== 'clear')
    if (visible.length === 0) return ''
    return ` · ${visible.map((signal) => `${signal.label}: ${signal.summary}`).join(' · ')}`
  }
  const { goals, tasks, subTasks, tasksByGoalId, subTasksByTaskId } = splitMacroElements(elements)
  const transition = getLayoutTransition(preferences.animationProfile)
  const activeGoal = goals.find((goal) => goal.id === activeGoalId) ?? goals[0]
  const activeGoalTasks = activeGoal ? tasksByGoalId.get(activeGoal.id) ?? [] : []
  const activeTask =
    tasks.find((task) => task.id === activeTaskId) ??
    activeGoalTasks[0] ??
    tasks[0]
  const activeTaskSubTasks = activeTask ? subTasksByTaskId.get(activeTask.id) ?? [] : []
  const activeSubTask =
    subTasks.find((subTask) => subTask.id === activeSubTaskId) ??
    activeTaskSubTasks[0] ??
    subTasks[0]
  const checklistItems = visualItems.filter((item): item is DashboardChecklistVisualItem => item.kind === 'CHECKLIST' && !item.isUnscheduled)

  if (goals.length === 0) {
    if (isAccessRestricted) {
      return (
        <div className="grid min-h-80 place-items-center gap-1 text-center p-8">
          <p className="text-sm font-medium text-[var(--muted)]">{t('NLSScopeEmptyRestricted')}</p>
        </div>
      )
    }
    return (
      <div className="grid min-h-80 place-items-center gap-1 text-center">
        <p className="text-sm font-black uppercase tracking-[0.18em] text-[var(--muted)]">Runway clear</p>
        <p className="text-xs text-[var(--muted)]">No goals scheduled in this workspace yet.</p>
      </div>
    )
  }

  const renderBar = ({
    item,
    index,
    rowIndex,
    children,
    childBarsOpenParent = false,
  }: {
    item: TimelineElement
    index: number
    rowIndex: number
    children?: TimelineElement[]
    childBarsOpenParent?: boolean
  }) => {
    const isPathOnly = pathOnlyElementIds.has(item.id)
    const progress = calculateElementProgress(item.id)
    const isSelected = selectedElementId === item.id
    const duration = Math.max(1, viewEndMs - viewStartMs)
    const itemStart = elementStartMs(item)
    const itemEnd = Math.max(itemStart + DAY_MS, elementEndMs(item))
    const leftPercent = ((itemStart - viewStartMs) / duration) * 100
    const widthPercent = Math.max(0.75, ((itemEnd - itemStart) / duration) * 100)

    if (isPathOnly) {
      return (
        <PathOnlyRow
          key={item.id}
          title={item.title}
          variant="timeline"
          style={{
            left: `${leftPercent}%`,
            width: `${Math.min(100, widthPercent)}%`,
            top: rowIndex * ROW_HEIGHT + 8,
            height: BAR_HEIGHT,
          }}
        />
      )
    }
    const labelOffsetPercent = leftPercent < 0
      ? clamp((-leftPercent / Math.max(1, widthPercent)) * 100, 0, 82)
      : 0
    const hasChildStrips = Boolean(children?.length)
    const labelHeight = hasChildStrips ? BAR_HEIGHT - CHILD_STRIP_HEIGHT : BAR_HEIGHT
    const readiness = readinessForElement(item)
    const readinessRing = readiness.status === 'blocked'
      ? 'ring-2 ring-[var(--danger)] shadow-[var(--shadow-card)]'
      : readiness.status === 'warning'
        ? 'ring-2 ring-[var(--warning)] shadow-[var(--shadow-card)]'
        : 'ring-[var(--border-subtle)]'
    const hasReadinessWarning = readiness.status !== 'clear'

    return (
      <motion.div
        key={item.id}
        layout
        layoutId={item.id}
        transition={transition}
        role="button"
        tabIndex={0}
        aria-label={`${item.type.toLowerCase()} ${item.title}, ${progress}% complete`}
        className={`absolute cursor-pointer overflow-hidden rounded-md shadow-md ring-1 transition-[filter,box-shadow] duration-150 hover:brightness-[1.08] ${
          isSelected
            ? 'ring-4 ring-[var(--border-strong)] shadow-[var(--shadow-card)]'
            : `${readinessRing} hover:shadow-[var(--shadow-card)]`
        }`}
        style={{
          left: `${leftPercent}%`,
          width: `${Math.min(100, widthPercent)}%`,
          top: rowIndex * ROW_HEIGHT + 8,
          height: BAR_HEIGHT,
          backgroundColor: getElementColor(item, index),
        }}
        onClick={(event) => {
          event.stopPropagation()
          onElementClick(item)
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.stopPropagation()
          onElementClick(item)
        }}
        data-hover-info={`${getHoverTitle(item)}${readinessTooltipSuffix(readiness)}`}
        data-orbit-target="event-marker"
        data-event-id={item.id}
        data-orbit-title={item.title}
        data-resource-status={readiness.status}
      >
        {hasReadinessWarning && (
          <span
            aria-hidden
            className={`absolute right-1 top-1 z-10 h-2.5 w-2.5 rounded-full border border-[var(--border-subtle)] ${
              readiness.status === 'blocked' ? 'bg-[var(--danger)] shadow-[var(--shadow-card)]' : 'bg-[var(--warning)] shadow-[var(--shadow-card)]'
            }`}
            title={readiness.signals.filter((signal) => signal.severity !== 'clear').map((signal) => signal.label).join(' · ')}
          />
        )}
        <div
          className="relative flex items-center justify-between gap-2 px-2 text-[10px] font-black text-[var(--text-strong)]"
          style={{
            height: labelHeight,
            transform: `translateX(${labelOffsetPercent}%)`,
            maxWidth: `${Math.max(18, 100 - labelOffsetPercent)}%`,
          }}
        >
          <span className="min-w-0 truncate">{item.title}</span>
          <span className="shrink-0">{progress}%</span>
        </div>
        {!hasChildStrips && progress > 0 && (
          <span
            aria-hidden
            className="pointer-events-none absolute bottom-0 left-0 h-[3px] rounded-r-sm bg-[var(--text-strong)]/80"
            style={{ width: `${Math.min(100, progress)}%` }}
          />
        )}
        {hasChildStrips && children && (
          <div className="relative bg-[var(--control)]/40" style={{ height: CHILD_STRIP_HEIGHT }}>
            {children.map((child, childIndex) => (
              <motion.div
                key={child.id}
                layout
                layoutId={child.id}
                transition={transition}
                role={childBarsOpenParent ? undefined : 'button'}
                tabIndex={childBarsOpenParent ? undefined : 0}
                className={`absolute top-0 h-full rounded-none ${childBarsOpenParent ? 'pointer-events-none' : 'cursor-pointer'} ${
                  !childBarsOpenParent && selectedElementId === child.id ? 'ring-4 ring-[var(--border-strong)] shadow-[var(--shadow-card)]' : ''
                }`}
                style={{
                  ...getSubTaskStyle(child, item),
                  backgroundColor: getElementColor(child, childIndex),
                }}
                onClick={
                  childBarsOpenParent
                    ? undefined
                    : (event) => {
                        event.stopPropagation()
                        onElementClick(child)
                      }
                }
                onKeyDown={
                  childBarsOpenParent
                    ? undefined
                    : (event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return
                        event.stopPropagation()
                        onElementClick(child)
                      }
                }
                data-hover-info={childBarsOpenParent ? undefined : getHoverTitle(child)}
              />
            ))}
          </div>
        )}
      </motion.div>
    )
  }

  const resolveParentRow = (
    item: DashboardChecklistVisualItem,
    rowByElementId: ReadonlyMap<string, number>,
  ): number | undefined =>
    rowByElementId.get(item.parentId ?? '') ?? rowByElementId.get(item.rootGoalId ?? '')

  const renderChecklistMarkers = (
    items: DashboardChecklistVisualItem[],
    rowByElementId: ReadonlyMap<string, number>,
  ) => {
    const duration = Math.max(1, viewEndMs - viewStartMs)
    // Only INDIVIDUAL items belong on the keyframe layer. Recurring masters
    // get rendered by the cadence trail below.
    return items
      .filter((item) => item.checklistCadence === 'INDIVIDUAL')
      .map((item) => {
        const dateMs = checklistDateMs(item)
        const parentRow = resolveParentRow(item, rowByElementId)
        if (dateMs === null || parentRow === undefined) return null
        const leftPercent = clamp(((dateMs - viewStartMs) / duration) * 100, 0.5, 99.5)
        const style = markerStyleFor(item)
        return (
          <Pressable
            key={`checklist-marker-${item.id}`}
            className={`absolute z-30 h-4 w-4 -translate-x-1/2 rotate-45 cursor-pointer rounded-[3px] border shadow-md transition-transform hover:scale-125 ${style.fill} ${style.border} ${style.opacity}`}
            style={{ left: `${leftPercent}%`, top: parentRow * ROW_HEIGHT + BAR_HEIGHT + 3 }}
            aria-label={`Open checklist ${item.title}`}
            title={`${style.label} · ${item.title}`}
            data-no-dashboard-drag="true"
            data-hover-info={`${style.label} · ${item.title}${item.assigneeInitials ? ` · ${item.assigneeInitials}` : ''}`}
            onClick={(event) => {
              event.stopPropagation()
              onChecklistClick(item.id)
            }}
          >
            <span className="sr-only">{item.title}</span>
          </Pressable>
        )
      })
  }

  /** Cadence trails: one horizontal strip of dots per recurring source,
   *  anchored just above the child-strip band on the parent row. Dot count
   *  is bounded by the projection layer (max 12 per source). */
  const renderCadenceTrails = (
    items: DashboardChecklistVisualItem[],
    rowByElementId: ReadonlyMap<string, number>,
  ) => {
    const duration = Math.max(1, viewEndMs - viewStartMs)
    const trails: ReactNode[] = []
    for (const item of items) {
      if (item.checklistCadence === 'INDIVIDUAL') continue
      const occurrences = recurringOccurrencesById.get(item.id)
      if (!occurrences || occurrences.length === 0) continue
      const parentRow = resolveParentRow(item, rowByElementId)
      if (parentRow === undefined) continue
      const trailTop = parentRow * ROW_HEIGHT + BAR_HEIGHT + 10
      trails.push(
        <div
          key={`cadence-trail-${item.id}`}
          className="pointer-events-none absolute left-0 right-0 z-20"
          style={{ top: trailTop, height: CADENCE_TRAIL_HEIGHT }}
          aria-hidden
        >
          {occurrences.map((occ) => {
            const anchorMs = occ.cadence === 'WEEKLY'
              ? (occ.occurrenceStartMs + occ.occurrenceEndMs) / 2
              : occ.occurrenceStartMs
            const leftPercent = ((anchorMs - viewStartMs) / duration) * 100
            if (leftPercent < -1 || leftPercent > 101) return null
            const width = occ.cadence === 'WEEKLY' ? 'w-2' : 'w-1.5'
            const height = occ.cadence === 'WEEKLY' ? 'h-2' : 'h-1.5'
            return (
              <span
                key={`${item.id}-${occ.occurrenceKey}`}
                className={`pointer-events-auto absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border ${width} ${height} ${cadenceDotClass(occ.state)}`}
                style={{ left: `${leftPercent}%` }}
                title={`${item.title} · ${occ.cadence === 'DAILY' ? 'Daily' : 'Weekly'} · ${occ.state} · ${occ.occurrenceKey}`}
                data-hover-info={`${item.title} · ${occ.cadence === 'DAILY' ? 'Daily' : 'Weekly'} · ${occ.state} · ${occ.occurrenceKey}`}
              />
            )
          })}
        </div>,
      )
    }
    return trails
  }

  /** Overdue spillover: when incomplete INDIVIDUAL checklists exist before
   *  viewStartMs on a visible parent row, render a small "N" chip on the
   *  left edge of that row. Surfaces overdue that has scrolled out of view. */
  const renderOverdueSpillover = (
    items: DashboardChecklistVisualItem[],
    rowByElementId: ReadonlyMap<string, number>,
  ) => {
    const countByRow = new Map<number, number>()
    for (const item of items) {
      if (item.checklistCadence !== 'INDIVIDUAL') continue
      if (item.isCompleted) continue
      const dateMs = checklistDateMs(item)
      if (dateMs === null || dateMs >= viewStartMs) continue
      const parentRow = resolveParentRow(item, rowByElementId)
      if (parentRow === undefined) continue
      countByRow.set(parentRow, (countByRow.get(parentRow) ?? 0) + 1)
    }
    return [...countByRow.entries()].map(([rowIndex, count]) => (
      <span
        key={`overdue-spill-${rowIndex}`}
        className="pointer-events-none absolute left-1 z-40 rounded-full border border-[var(--warning)] bg-[var(--warning-soft)] px-1.5 text-[9px] font-black leading-4 text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))] shadow"
        style={{ top: rowIndex * ROW_HEIGHT + 10 }}
        title={`${count} overdue checklist${count === 1 ? '' : 's'} before the visible window`}
      >
        ↤{count}
      </span>
    ))
  }

  /** Vertical today line + small "Today" chip at the top. Rendered only when
   *  today falls inside the current window — the projection returns null
   *  otherwise so views don't clamp a stale marker to an edge. */
  const renderTodayLine = (trackHeight: number) => {
    if (!todayContext.isTodayInWindow || todayContext.todayRatio === null) return null
    const leftPercent = todayContext.todayRatio * 100
    const reducedMotion = preferences.animationProfile === 'REDUCED_MOTION'
    return (
      <>
        <span
          aria-hidden
          className="pointer-events-none absolute top-0 z-30 w-px"
          style={{
            left: `${leftPercent}%`,
            height: trackHeight,
            background:
              'linear-gradient(to bottom, color-mix(in srgb, var(--info) 95%, transparent), color-mix(in srgb, var(--info) 40%, transparent) 55%, color-mix(in srgb, var(--info) 5%, transparent))',
          }}
        />
        <CanvasOverlay anchor="viewport">
          <motion.span
            className="pointer-events-none absolute top-0 z-40 -translate-x-1/2 rounded-b border border-[var(--info)] bg-[var(--info-soft)] px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider text-[color-mix(in_srgb,var(--info)_60%,var(--text-strong))] shadow-[var(--shadow-card)]"
            style={{ left: `${leftPercent}%` }}
            animate={reducedMotion ? undefined : { opacity: [1, 0.72, 1] }}
            transition={reducedMotion ? undefined : { repeat: Infinity, duration: 2.6, ease: 'easeInOut' }}
          >
            Today
          </motion.span>
        </CanvasOverlay>
      </>
    )
  }

  /** Track chrome: aligned vertical grid (from TimeScale markers), weekend
   *  bands (auto-hidden past 60 visible days), and row stripes. Purely
   *  visual scaffolding beneath every bar. */
  const renderTrackChrome = (rowCount: number) => {
    const markers = buildScaleMarkers(viewStartMs, viewEndMs, { zoom: canvasTransform.zoom })
    const weekendBands = buildWeekendBands(viewStartMs, viewEndMs)
    const rows = Math.max(1, rowCount)
    return (
      <>
        {Array.from({ length: rows }).map((_, i) =>
          i % 2 === 1 ? (
            <span
              key={`stripe-${i}`}
              aria-hidden
              className="pointer-events-none absolute inset-x-0 z-0 bg-[var(--text-strong)]/[0.025]"
              style={{ top: i * ROW_HEIGHT + 4, height: ROW_HEIGHT - 8 }}
            />
          ) : null,
        )}
        {weekendBands.map((band, i) => (
          <span
            key={`weekend-${i}`}
            aria-hidden
            className="pointer-events-none absolute inset-y-0 z-0 bg-[var(--text-strong)]/[0.03]"
            style={{ left: band.left, width: band.width }}
          />
        ))}
        {markers.map((marker) => (
          <span
            key={`grid-${marker.id}`}
            aria-hidden
            className="pointer-events-none absolute inset-y-0 z-0 w-px bg-[var(--text-strong)]/[0.06]"
            style={{ left: marker.left }}
          />
        ))}
      </>
    )
  }

  if (currentLevel === 'dashboard') {
    const rows = packRows(goals)
    const rowByElementId = new Map<string, number>()
    rows.forEach((row, rowIndex) => row.forEach((item) => rowByElementId.set(item.id, rowIndex)))
    return (
      <section id="timeline-view" className="grid gap-2">
        <TimeScale startMs={viewStartMs} endMs={viewEndMs} />
        <motion.div layout className="p-1">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[var(--muted)]">
                Runway · {goals.length} goal{goals.length === 1 ? '' : 's'}
              </p>
              <p className="mt-0.5 text-[11px] font-bold text-[var(--muted)]">{formatElementsRange(goals)}</p>
            </div>
            <span className="rounded-md bg-[var(--control)]/70 px-2 py-1 text-sm font-black text-[var(--text-strong)] shadow-inner">
              {calculateAverageProgress(goals, calculateElementProgress)}%
            </span>
          </div>

          <div
            className="relative overflow-hidden rounded-md border border-[var(--border)] bg-gradient-to-b from-[var(--control)]/80 to-[var(--control)]/55 shadow-inner"
            style={{ height: Math.max(1, rows.length) * ROW_HEIGHT + 12, overscrollBehavior: 'contain', touchAction: 'none' }}
            data-timeline-track="true"
          >
            {renderTrackChrome(rows.length)}
            {rows.map((row, rowIndex) =>
              row.map((item, itemIndex) =>
                renderBar({
                  item,
                  index: rowIndex + itemIndex,
                  rowIndex,
                  children: tasksByGoalId.get(item.id) ?? [],
                  childBarsOpenParent: true,
                }),
              ),
            )}
            {renderChecklistMarkers(checklistItems, rowByElementId)}
            {renderCadenceTrails(checklistItems, rowByElementId)}
            {renderOverdueSpillover(checklistItems, rowByElementId)}
            {renderTodayLine(Math.max(1, rows.length) * ROW_HEIGHT + 12)}
          </div>
        </motion.div>
      </section>
    )
  }

  const visibleGoals = activeGoal ? [activeGoal] : goals

  return (
    <section id="timeline-view" data-orbit-target="canvas" className="grid gap-2">
      <TimeScale startMs={viewStartMs} endMs={viewEndMs} />
      {visibleGoals.map((goal) => {
        const goalTasks = tasksByGoalId.get(goal.id) ?? []
        const visibleItems =
          currentLevel === 'goal'
            ? goalTasks
            : currentLevel === 'task'
              ? activeTaskSubTasks
              : currentLevel === 'subtask'
                ? activeSubTask ? [activeSubTask] : activeTaskSubTasks
              : tasks
        const rows = packRows(visibleItems)
        const rowByElementId = new Map<string, number>()
        rows.forEach((row, rowIndex) => row.forEach((item) => rowByElementId.set(item.id, rowIndex)))
        const visibleChecklistItems = checklistItems.filter((item) =>
          item.parentId ? rowByElementId.has(item.parentId) : item.rootGoalId === goal.id,
        )
        const headerTitle =
          currentLevel === 'goal'
            ? goal.title
            : currentLevel === 'task'
              ? activeTask?.title ?? `${visibleItems.length} subtasks`
              : activeSubTask?.title ?? `${visibleItems.length} subtasks`
        const headerRange =
          currentLevel === 'goal'
            ? formatElementRange(goal)
            : currentLevel === 'task'
              ? activeTask ? formatElementRange(activeTask) : formatElementsRange(visibleItems)
              : activeSubTask ? formatElementRange(activeSubTask) : formatElementsRange(visibleItems)
        const headerProgress =
          currentLevel === 'goal'
            ? calculateElementProgress(goal.id)
            : currentLevel === 'task' && activeTask
              ? calculateElementProgress(activeTask.id)
              : currentLevel === 'subtask' && activeSubTask
                ? calculateElementProgress(activeSubTask.id)
                : calculateAverageProgress(visibleItems, calculateElementProgress)

        return (
          <motion.div
            key={goal.id}
            layout
            className={`p-1 ${
              selectedElementId === goal.id ? 'rounded-lg shadow-[var(--shadow-card)]' : ''
            }`}
          >
            <div data-orbit-target="band-header" data-band-id={goal.id} data-orbit-title={headerTitle} className="mb-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[var(--muted)]">
                  {currentLevel === 'goal' ? `Tasks · ${visibleItems.length}` : `Subtasks · ${visibleItems.length}`}
                </p>
                <h3 className="truncate text-base font-black text-[var(--text-strong)]">
                  {headerTitle}
                </h3>
                <p className="mt-0.5 text-[11px] font-bold text-[var(--muted)]">{headerRange}</p>
              </div>
              <span className="rounded-md bg-[var(--control)]/70 px-2 py-1 text-sm font-black text-[var(--text-strong)] shadow-inner">
                {headerProgress}%
              </span>
            </div>

            <div
              className="relative overflow-hidden rounded-md border border-[var(--border)] bg-gradient-to-b from-[var(--control)]/80 to-[var(--control)]/55 shadow-inner"
              style={{ height: Math.max(1, rows.length) * ROW_HEIGHT + 12, overscrollBehavior: 'contain', touchAction: 'none' }}
              data-timeline-track="true"
              data-hover-info={getHoverTitle(goal)}
            >
              {renderTrackChrome(rows.length)}
              <div
                className="pointer-events-none absolute inset-y-2 rounded-md border border-[var(--border-subtle)] bg-[var(--control)]/30"
                style={getRangeStyle(goal, viewStartMs, viewEndMs)}
              />
              {rows.map((row, rowIndex) =>
                row.map((item, itemIndex) =>
                  renderBar({
                    item,
                    index: rowIndex + itemIndex,
                    rowIndex,
                    children: item.type === 'TASK' ? subTasksByTaskId.get(item.id) ?? [] : undefined,
                    childBarsOpenParent: currentLevel === 'goal',
                  }),
                ),
              )}
              {renderChecklistMarkers(visibleChecklistItems, rowByElementId)}
              {renderCadenceTrails(visibleChecklistItems, rowByElementId)}
              {renderOverdueSpillover(visibleChecklistItems, rowByElementId)}
              {renderTodayLine(Math.max(1, rows.length) * ROW_HEIGHT + 12)}
            </div>
          </motion.div>
        )
      })}
    </section>
  )
}
